import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Resource {
  id: string;
  title: string;
  description: string;
  category: string;
  fileUrl: string;
  fileSize: string;
  fileName: string;
  fileType: string;
}

const Resources = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [resources, setResources] = useState<Resource[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [uploadFormData, setUploadFormData] = useState({
    title: '',
    description: '',
    category: '啓蒙活動'
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [newCategory, setNewCategory] = useState('');

  useEffect(() => {
    const auth = sessionStorage.getItem('resourcesAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
    loadResources();
    loadCategories();
  }, []);

  const loadCategories = () => {
    const stored = localStorage.getItem('resourceCategories');
    if (stored) {
      setCategories(JSON.parse(stored));
    } else {
      const defaultCategories = ['啓蒙活動', '地域連携', 'データシート', 'その他'];
      localStorage.setItem('resourceCategories', JSON.stringify(defaultCategories));
      setCategories(defaultCategories);
    }
  };

  const loadResources = () => {
    const stored = localStorage.getItem('heartFailureResources');
    if (stored) {
      setResources(JSON.parse(stored));
    } else {
      // デフォルトのサンプルリソース
      const defaultResources: Resource[] = [
        {
          id: '1',
          title: '心不全啓蒙活動用チラシ',
          description: '患者さん向けの心不全に関する基本情報と予防方法をまとめたチラシです。',
          category: '啓蒙活動',
          fileUrl: '#',
          fileSize: '2.3 MB',
          fileName: 'heart-failure-flyer.pdf',
          fileType: 'pdf'
        },
        {
          id: '2',
          title: '地域連携用紹介状テンプレート',
          description: '医療機関間での患者紹介時に使用する標準フォーマットです。',
          category: '地域連携',
          fileUrl: '#',
          fileSize: '1.8 MB',
          fileName: 'referral-template.pdf',
          fileType: 'pdf'
        },
        {
          id: '3',
          title: '心不全管理データシート',
          description: '患者さんの日常管理に役立つ記録シートと管理ガイドラインです。',
          category: 'データシート',
          fileUrl: '#',
          fileSize: '3.1 MB',
          fileName: 'management-sheet.pdf',
          fileType: 'pdf'
        }
      ];
      localStorage.setItem('heartFailureResources', JSON.stringify(defaultResources));
      setResources(defaultResources);
    }
  };

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'toranomon') {
      setIsAuthenticated(true);
      setIsAdminMode(false);
      sessionStorage.setItem('resourcesAuth', 'true');
      setError('');
      setPassword('');
    } else {
      setError('パスワードが正しくありません');
    }
  };

  const handleAdminLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'cr.toranomon') {
      setIsAuthenticated(true);
      setIsAdminMode(true);
      sessionStorage.setItem('resourcesAuth', 'true');
      setError('');
      setPassword('');
    } else {
      setError('パスワードが正しくありません');
    }
  };

  const getFileIcon = (fileType: string) => {
    const type = fileType.toLowerCase();
    if (type === 'pdf') return { icon: 'ri-file-pdf-line', color: 'text-red-600', bg: 'bg-red-100' };
    if (type === 'doc' || type === 'docx') return { icon: 'ri-file-word-line', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (type === 'xls' || type === 'xlsx') return { icon: 'ri-file-excel-line', color: 'text-green-600', bg: 'bg-green-100' };
    if (type === 'ppt' || type === 'pptx') return { icon: 'ri-file-ppt-line', color: 'text-orange-600', bg: 'bg-orange-100' };
    if (['jpg', 'jpeg', 'png', 'gif'].includes(type)) return { icon: 'ri-image-line', color: 'text-purple-600', bg: 'bg-purple-100' };
    return { icon: 'ri-file-line', color: 'text-gray-600', bg: 'bg-gray-100' };
  };

  const getFileType = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    return extension;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'image/jpeg',
        'image/png',
        'image/gif'
      ];
      
      if (allowedTypes.includes(file.type)) {
        setSelectedFile(file);
      } else {
        alert('対応していないファイル形式です。PDF、Word、Excel、PowerPoint、画像ファイル（JPG、PNG、GIF）のみアップロード可能です。');
        e.target.value = '';
      }
    }
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile) {
      alert('ファイルを選択してください');
      return;
    }

    // ファイルをBase64に変換して保存（実際の実装ではサーバーにアップロード）
    const reader = new FileReader();
    reader.onload = (event) => {
      const fileData = event.target?.result as string;
      const fileSizeInMB = (selectedFile.size / (1024 * 1024)).toFixed(1);
      const fileType = getFileType(selectedFile.name);
      
      const newResource: Resource = {
        id: Date.now().toString(),
        title: uploadFormData.title,
        description: uploadFormData.description,
        category: uploadFormData.category,
        fileUrl: fileData,
        fileSize: `${fileSizeInMB} MB`,
        fileName: selectedFile.name,
        fileType: fileType
      };

      const updatedResources = [...resources, newResource];
      localStorage.setItem('heartFailureResources', JSON.stringify(updatedResources));
      setResources(updatedResources);
      
      // フォームをリセット
      setUploadFormData({ title: '', description: '', category: categories[0] || '啓蒙活動' });
      setSelectedFile(null);
      setShowUploadForm(false);
      alert('ファイルをアップロードしました');
    };
    
    reader.readAsDataURL(selectedFile);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('この資料を削除しますか？')) {
      const updatedResources = resources.filter(r => r.id !== id);
      localStorage.setItem('heartFailureResources', JSON.stringify(updatedResources));
      setResources(updatedResources);
    }
  };

  const handleDownload = (resource: Resource) => {
    if (resource.fileUrl.startsWith('data:')) {
      // Base64データの場合
      const link = document.createElement('a');
      link.href = resource.fileUrl;
      link.download = resource.fileName;
      link.click();
    } else {
      // 通常のURLの場合
      window.open(resource.fileUrl, '_blank');
    }
  };

  const handleAddCategory = () => {
    if (!newCategory.trim()) {
      alert('カテゴリー名を入力してください');
      return;
    }
    if (categories.includes(newCategory.trim())) {
      alert('このカテゴリーは既に存在します');
      return;
    }
    const updatedCategories = [...categories, newCategory.trim()];
    localStorage.setItem('resourceCategories', JSON.stringify(updatedCategories));
    setCategories(updatedCategories);
    setNewCategory('');
  };

  const handleDeleteCategory = (category: string) => {
    if (window.confirm(`カテゴリー「${category}」を削除しますか？\nこのカテゴリーに属する資料は「その他」に移動されます。`)) {
      // カテゴリーを削除
      const updatedCategories = categories.filter(c => c !== category);
      localStorage.setItem('resourceCategories', JSON.stringify(updatedCategories));
      setCategories(updatedCategories);

      // このカテゴリーの資料を「その他」に移動
      const updatedResources = resources.map(r => 
        r.category === category ? { ...r, category: 'その他' } : r
      );
      localStorage.setItem('heartFailureResources', JSON.stringify(updatedResources));
      setResources(updatedResources);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-24 pb-20">
          <div className="max-w-md mx-auto px-6">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-lock-line text-3xl text-teal-600"></i>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">資料ダウンロード</h1>
                <p className="text-gray-600 text-sm">このページにアクセスするにはパスワードが必要です</p>
              </div>

              <div className="mb-6">
                <div className="flex gap-2 border-b border-gray-200">
                  <button
                    onClick={() => setIsAdminMode(false)}
                    className={`flex-1 py-3 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      !isAdminMode
                        ? 'text-teal-600 border-b-2 border-teal-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    閲覧モード
                  </button>
                  <button
                    onClick={() => setIsAdminMode(true)}
                    className={`flex-1 py-3 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      isAdminMode
                        ? 'text-teal-600 border-b-2 border-teal-600'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    管理者モード
                  </button>
                </div>
              </div>

              <form onSubmit={isAdminMode ? handleAdminLogin : handleLogin} className="space-y-6">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    パスワード
                  </label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="パスワードを入力してください"
                    required
                  />
                  {error && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>
                      {error}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full bg-teal-600 text-white py-3 rounded-lg font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  ログイン
                </button>
              </form>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-6">
            資料ダウンロード
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            心不全に関する啓蒙活動用チラシや地域連携用の資料をダウンロードいただけます
          </p>
        </div>
      </section>

      {/* Admin Upload Section */}
      {isAdminMode && (
        <section className="py-8 bg-teal-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="text-xl font-bold text-gray-900">管理者モード</h2>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCategoryManager(!showCategoryManager);
                    setShowUploadForm(false);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-folder-settings-line"></i>
                  カテゴリー管理
                </button>
                <button
                  onClick={() => {
                    setShowUploadForm(!showUploadForm);
                    setShowCategoryManager(false);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-upload-line"></i>
                  {showUploadForm ? 'キャンセル' : '新しい資料をアップロード'}
                </button>
              </div>
            </div>

            {/* Category Manager */}
            {showCategoryManager && (
              <div className="bg-white rounded-2xl p-6 shadow-sm mb-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4">カテゴリー管理</h3>
                
                {/* Add Category */}
                <div className="mb-6 flex gap-2">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    placeholder="新しいカテゴリー名を入力"
                  />
                  <button
                    onClick={handleAddCategory}
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                  >
                    追加
                  </button>
                </div>

                {/* Category List */}
                <div className="space-y-2">
                  {categories.map((category) => (
                    <div
                      key={category}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <span className="text-sm font-medium text-gray-900">{category}</span>
                      <button
                        onClick={() => handleDeleteCategory(category)}
                        className="text-red-500 hover:text-red-700 cursor-pointer p-1"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Form */}
            {showUploadForm && (
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <form onSubmit={handleUpload} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        タイトル
                      </label>
                      <input
                        type="text"
                        value={uploadFormData.title}
                        onChange={(e) => setUploadFormData({ ...uploadFormData, title: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="資料のタイトルを入力"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        カテゴリ
                      </label>
                      <select
                        value={uploadFormData.category}
                        onChange={(e) => setUploadFormData({ ...uploadFormData, category: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      >
                        {categories.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      説明
                    </label>
                    <textarea
                      value={uploadFormData.description}
                      onChange={(e) => setUploadFormData({ ...uploadFormData, description: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                      placeholder="資料の説明を入力"
                      rows={3}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ファイル
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif"
                      onChange={handleFileSelect}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                      required
                    />
                    {selectedFile && (
                      <p className="mt-2 text-sm text-gray-600 flex items-center gap-2">
                        <i className={`${getFileIcon(getFileType(selectedFile.name)).icon} ${getFileIcon(getFileType(selectedFile.name)).color}`}></i>
                        {selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(1)} MB)
                      </p>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      対応形式：PDF、Word、Excel、PowerPoint、画像ファイル（JPG、PNG、GIF）
                    </p>
                  </div>

                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
                  >
                    アップロード
                  </button>
                </form>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Resources Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-8">
            <p className="text-gray-600 text-sm">
              全 {resources.length} 件の資料
            </p>
            <button
              onClick={() => {
                sessionStorage.removeItem('resourcesAuth');
                setIsAuthenticated(false);
                setIsAdminMode(false);
              }}
              className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-2 cursor-pointer transition-colors"
            >
              <i className="ri-logout-box-line"></i>
              ログアウト
            </button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((resource) => {
              const fileIcon = getFileIcon(resource.fileType);
              return (
                <div
                  key={resource.id}
                  className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 ${fileIcon.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        <i className={`${fileIcon.icon} text-2xl ${fileIcon.color}`}></i>
                      </div>
                      <span className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full whitespace-nowrap">
                        {resource.category}
                      </span>
                    </div>

                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                      {resource.title}
                    </h3>
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {resource.description}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="text-xs text-gray-500 flex items-center gap-1">
                        <i className="ri-file-line"></i>
                        {resource.fileSize}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleDownload(resource)}
                          className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium text-sm cursor-pointer transition-colors"
                        >
                          ダウンロード
                          <i className="ri-download-line"></i>
                        </button>
                        {isAdminMode && (
                          <button
                            onClick={() => handleDelete(resource.id)}
                            className="text-red-500 hover:text-red-700 cursor-pointer p-1"
                          >
                            <i className="ri-delete-bin-line text-lg"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Info Box */}
          <div className="mt-12 bg-blue-50 rounded-2xl p-6 border border-blue-100">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-xl text-blue-600"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900 mb-2">ご利用にあたって</h3>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• 資料は医療機関および患者さん向けの情報提供を目的としています</li>
                  <li>• 資料の内容は予告なく変更される場合があります</li>
                  <li>• <strong>資料を転用・使用する場合は、事前に許可が必要です。お問い合わせページよりご連絡ください</strong></li>
                  <li>• ご不明な点がございましたら、お問い合わせページよりご連絡ください</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Resources;
